import React, { useState, useMemo } from "react";
import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import { MGridColumnDefinition } from "../MGrid";
import {
  QaTimeCardResponse,
  SchedulingTimeCardResponse,
} from "../../../interfaces/Mueller.Business.Models";
import xlsx from "xlsx";
import { formatWorksheetColumns } from "../Functions.js";
import MSpinner from "../MSpinner";
import TimeLogTableGroup, {
  SortDirection,
  ExpandCollapseAllStates,
  SortedColumn,
  expandCollapseColWidth,
  getRowValue,
  compareWithObjects,
} from "./TimeLogTableGroup";

// TODO: this could probably be a more generic grouped table component but there's simply no time atm

interface GroupedTimeLogTableProps {
  columnDefinitions: MGridColumnDefinition[]; // Reusing this for now as this is replacing an MGrid component
  data: QaTimeCardResponse[] | SchedulingTimeCardResponse[];
  onRowClick: (internetUserId: number) => void;
  downloadFileName: string;
  fetchingData: boolean;
}

const GroupedTimeLogTable = (props: GroupedTimeLogTableProps) => {
  const [sort, setSort] = useState<SortedColumn>(undefined);
  const [expandCollapseAll, setExpandCollapseAll] = useState<ExpandCollapseAllStates>(ExpandCollapseAllStates.COLLAPSE);
  const visibleColumnDefs = props.columnDefinitions?.filter(col => col.visible);

  const handleSort = (key: string, dataType: string) => {
    if (sort && sort.key === key) {
      switch (sort.direction) {
        case SortDirection.ASC:
          setSort((prevState) => ({ ...prevState, direction: SortDirection.DES }));
          break;
        case SortDirection.DES:
          setSort((prevState) => ({ ...prevState, direction: SortDirection.NONE }));
          break;
        case SortDirection.NONE:
        default:
          setSort((prevState) => ({ ...prevState, direction: SortDirection.ASC }));
          break;
      }
    } else {
      setSort({ key: key, direction: SortDirection.ASC, dataType: dataType });
    }
  };

  const getHeaderIconForSort = (sortDirection: SortDirection) => {
    switch (sortDirection) {
      case SortDirection.ASC:
        return "arrow_drop_up";
      case SortDirection.DES:
        return "arrow_drop_down";
      case SortDirection.NONE:
        return "";
    }
  };

  const onDownload = () => {
    const displayColumns = props.columnDefinitions.filter((c) => !c.hideFromExport);
    const cols = displayColumns.map((c) => c.name);
    const allEmployees = props.data.flatMap((d) => d.employeeList || []);
    const rows = allEmployees.map((row) =>
      displayColumns.reduce((acc, cd) => {
        acc[cd.name] = typeof cd.dataSource === "function" ? cd.dataSource(row) : getRowValue(row, cd.key, cd.dataType);
        return acc;
      }, {})
    );

    const wb = xlsx.utils.book_new();
    let ws = xlsx.utils.json_to_sheet(rows, { header: cols });
    formatWorksheetColumns(displayColumns, ws);
    xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");
    xlsx.writeFile(wb, `${props.downloadFileName}.xlsx`);
  };

  const toggleExpandCollapseAll = () => {
    switch (expandCollapseAll) {
      case ExpandCollapseAllStates.EXPAND:
        setExpandCollapseAll(ExpandCollapseAllStates.COLLAPSE);
        break;
      case ExpandCollapseAllStates.COLLAPSE:
        setExpandCollapseAll(ExpandCollapseAllStates.EXPAND);
        break;
    }
  };

  let tableData = useMemo(() => {
    // The first entry is the totals and has no employeeList
    let data = [...props.data.slice(1)];
    if (sort && sort.direction !== SortDirection.NONE) {
      const comparer = (
        a: QaTimeCardResponse | SchedulingTimeCardResponse,
        b: QaTimeCardResponse | SchedulingTimeCardResponse
      ) => {
        // If there's no datatype its a string
        switch (sort?.direction) {
          case SortDirection.ASC:
            return compareWithObjects(a, b, `totals.${sort.key}`);
          case SortDirection.DES:
            return compareWithObjects(b, a, `totals.${sort.key}`);
          case SortDirection.NONE:
          default:
            return 0;
        }
      };
      data.sort(comparer);
    }
    return data;
  }, [sort, props.data]);

  let totalsRow = useMemo(() => {
    if (props.data?.length) {
      return (
        <TableRow key={`expandCollapse-${props.data[0]?.totals.employeeName.firstLast}`} className="totals-row">
          {/* Add an extra column for the expand/collapse control */}
          <TableCell
            key={`expandCollapse-${props.data[0]?.totals.employeeName}`}
            style={{ minWidth: `${expandCollapseColWidth}px` }}
            className="m-table-data-column"
          />
          {visibleColumnDefs?.map(({ key, tooltip, dataSource, width, dataType }, i) => (
            <TableCell
              // @ts-ignore
              title={typeof tooltip === "string" ? tooltip : null}
              className="m-table-data-column"
              key={`${key}-${i}`}
              style={{ minWidth: `${width}px`, maxWidth: `${width}px`, textAlign: `${dataType ? "right" : "left"}` }}
            >
              {typeof dataSource === "function"
                ? dataSource(props.data[0]?.totals)
                : getRowValue(props.data[0]?.totals, dataSource, dataType)}
            </TableCell>
          ))}
        </TableRow>
      );
    }
  }, [props.data, props.columnDefinitions]);

  if (props.fetchingData) {
    return <MSpinner />;
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row-reverse", padding: "8px" }}>
        <Button onClick={onDownload} disabled={!props.data}>
          Download
        </Button>
      </div>
      <TableContainer component="div" className="m-table grouped-time-log-data-table">
        <Table>
        <TableHead>
          <TableRow className="header-row">
            {/* Cell for expand collapse column */}
            <TableCell
              title="Expand/Collapse"
              key="expandCollapse"
              onClick={toggleExpandCollapseAll}
              style={{ minWidth: `${expandCollapseColWidth}px`, cursor: "pointer" }}
            >
              <Icon color="primary">expand_circle_down</Icon>
            </TableCell>
            {visibleColumnDefs?.map(({ key, name, width, dataType, headerTooltip }, i) => (
              <TableCell
                // @ts-ignore
                title={headerTooltip}
                key={`${key}-${i}`}
                onClick={() => handleSort(key, dataType)}
                style={{ minWidth: `${width}px`, maxWidth: `${width}px`, cursor: "pointer" }}
              >
                {name}
                {sort && sort.key === key && sort.direction !== SortDirection.NONE && (
                  <Icon color="primary">{getHeaderIconForSort(sort.direction)}</Icon>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {totalsRow}
          {/* Add 1 for expand collapse column */}
          <TableCell colSpan={(visibleColumnDefs.length ?? 0) + 1} style={{ padding: "0px" }}>
            {tableData.map((d: SchedulingTimeCardResponse | QaTimeCardResponse, i: number) => (
              <TimeLogTableGroup
                key={`tableGroup-${i}`}
                columnDefinitions={visibleColumnDefs}
                data={d}
                sort={sort}
                expandCollapseState={expandCollapseAll}
                onRowClick={props.onRowClick}
              />
            ))}
          </TableCell>
        </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default GroupedTimeLogTable;
